'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function StatsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const stats = [
    {
      id: 'stat_years',
      icon: 'CalendarIcon',
      value: '5+',
      label: 'Years of Excellence',
    },
    {
      id: 'stat_students',
      icon: 'UserGroupIcon',
      value: '1000+',
      label: 'Students Trained',
    },
    {
      id: 'stat_courses',
      icon: 'BookOpenIcon',
      value: '10+',
      label: 'Industry Courses',
    },
    {
      id: 'stat_placement',
      icon: 'TrophyIcon',
      value: '90%',
      label: 'Success Rate',
    },
  ];

  return (
    <section ref={sectionRef} className="py-20 md:py-32 px-6 bg-gradient-to-br from-secondary via-secondary/95 to-primary/20">
      <div className="max-w-7xl mx-auto">
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-none text-white mb-4">
            Our <span className="text-primary">Impact</span>
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Numbers that reflect our commitment to excellence and student success
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={stat.id}
              className={`text-center ${
                isVisible ? 'animate-fadeInScale' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
                <Icon name={stat.icon as any} size={36} className="text-primary" />
              </div>
              <div className="font-display text-6xl md:text-7xl text-white mb-2">
                {stat.value}
              </div>
              <p className="text-sm uppercase tracking-wider text-white/80">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}